import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Alert, Container, Paper, Button } from '@mui/material';
import { ErrorOutline, CheckCircleOutline } from '@mui/icons-material';
import { useSsoCallback, getSsoRedirectUrl } from '@/hooks/auth/useSsoAuth';
import { getSsoErrorMessage } from '@/utils/errorUtils';
import { createLogger } from '@/utils/logger';

const log = createLogger('SsoCallback');

/**
 * Search params expected from the SSO callback URL
 * Backend redirects to: /auth/sso/callback?token=...&refreshToken=...&expiresAt=...&isNewUser=...
 * Or on error: /auth/sso/callback?error=...&errorDescription=...
 */
interface SsoCallbackSearch {
  token?: string;
  refreshToken?: string;
  expiresAt?: string;
  isNewUser?: string;
  error?: string;
  errorDescription?: string;
}

export const Route = createFileRoute('/auth/sso/callback')({
  component: SsoCallbackPage,
  validateSearch: (search: Record<string, unknown>): SsoCallbackSearch => {
    return {
      token: typeof search.token === 'string' ? search.token : undefined,
      refreshToken: typeof search.refreshToken === 'string' ? search.refreshToken : undefined,
      expiresAt: typeof search.expiresAt === 'string' ? search.expiresAt : undefined,
      isNewUser: typeof search.isNewUser === 'string' ? search.isNewUser : undefined,
      error: typeof search.error === 'string' ? search.error : undefined,
      errorDescription: typeof search.errorDescription === 'string' ? search.errorDescription : undefined,
    };
  },
});

export function SsoCallbackPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/auth/sso/callback' });
  const ssoCallbackMutation = useSsoCallback();
  // Destructure the stable `mutateAsync` method. Per TanStack Query's official
  // `no-unstable-deps` ESLint rule, the top-level object returned by
  // useMutation is NOT referentially stable, but its individual methods
  // (`mutate`, `mutateAsync`, `reset`) ARE — they are bound once on a
  // memoized MutationObserver instance (see `bindMethods()` in
  // @tanstack/query-core/src/mutationObserver.ts), so they keep the same
  // reference across renders.
  // See: https://tanstack.com/query/v5/docs/eslint/no-unstable-deps
  const { mutateAsync: ssoCallbackMutateAsync } = ssoCallbackMutation;
  const processedRef = useRef(false);

  // Track login success with local state (more reliable than mutation.isSuccess across re-renders)
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Capture tokens in a ref on first render - this preserves them even if URL changes
  const tokensRef = useRef<{
    token?: string;
    refreshToken?: string;
    expiresAt?: string;
    isNewUser?: string;
    error?: string;
    errorDescription?: string;
    captured: boolean;
  }>({ captured: false });

  // Capture tokens from search params on first render only
  if (!tokensRef.current.captured && (search.token || search.error)) {
    tokensRef.current = {
      token: search.token,
      refreshToken: search.refreshToken,
      expiresAt: search.expiresAt,
      isNewUser: search.isNewUser,
      error: search.error,
      errorDescription: search.errorDescription,
      captured: true,
    };
    log.debug('Captured tokens from URL');
  }

  // Use captured tokens (persists even if URL is cleared)
  const { token, refreshToken, expiresAt, isNewUser, error, errorDescription } = tokensRef.current;

  // Check if this is an error response
  const hasError = !!error;
  const hasTokens = !!token && !!refreshToken;

  // Process tokens effect - runs once when component mounts with tokens
  useEffect(() => {
    // Prevent double processing in React Strict Mode
    if (processedRef.current) {
      return;
    }

    // Handle error case
    if (hasError) {
      log.error('SSO authentication failed:', { error, errorDescription });
      processedRef.current = true;
      // Clear tokens from URL for security
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Handle success case - process tokens
    if (hasTokens) {
      processedRef.current = true;
      log.info('Processing SSO callback with tokens');

      // Use async/await pattern - more reliable than onSuccess callbacks
      const processTokens = async () => {
        try {
          await ssoCallbackMutateAsync({
            token,
            refreshToken,
            expiresAt,
            isNewUser: isNewUser === 'true',
          });
          log.info('SSO login successful, setting success state');
          setLoginSuccess(true);
        } catch (err) {
          log.error('Failed to process SSO callback:', err);
        }
      };

      processTokens();
    }
  }, [hasTokens, hasError, token, refreshToken, expiresAt, isNewUser, error, errorDescription, ssoCallbackMutateAsync]);

  // Navigation effect - runs when login succeeds
  useEffect(() => {
    if (loginSuccess) {
      const redirectTo = getSsoRedirectUrl();
      log.info('Navigating after SSO login', { redirectTo });
      navigate({ to: redirectTo, replace: true });
    }
  }, [loginSuccess, navigate]);

  // Handle navigation back to login on error
  const handleBackToLogin = () => {
    navigate({ to: '/preparer/login', replace: true });
  };

  // Error state
  if (hasError) {
    const errorMessage = getSsoErrorMessage(error, errorDescription || null);

    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: 64,
                  color: 'error.main',
                  mb: 2,
                }}
              />
              <Typography component="h1" variant="h5" fontWeight="bold" gutterBottom>
                Sign-in Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                {errorMessage}
              </Alert>
              {errorDescription && errorDescription !== error && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {errorDescription}
                </Typography>
              )}
              <Button variant="contained" fullWidth onClick={handleBackToLogin} sx={{ mt: 2 }}>
                Back to Sign In
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Processing state (including mutation error)
  if (ssoCallbackMutation.isError) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: 64,
                  color: 'error.main',
                  mb: 2,
                }}
              />
              <Typography component="h1" variant="h5" fontWeight="bold" gutterBottom>
                Sign-in Error
              </Typography>
              <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                Failed to complete sign-in. Please try again.
              </Alert>
              <Button variant="contained" fullWidth onClick={handleBackToLogin} sx={{ mt: 2 }}>
                Back to Sign In
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Success state (brief flash before navigation)
  if (loginSuccess) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CheckCircleOutline
            sx={{
              fontSize: 64,
              color: 'success.main',
              mb: 2,
            }}
          />
          <Typography variant="h6" color="text.secondary">
            Sign-in successful. Redirecting...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Loading/processing state
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          Completing sign-in...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we verify your credentials.
        </Typography>
      </Box>
    </Container>
  );
}
